import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useAuth } from '@/lib/use-auth';
import {
  decodeCycleMarkers,
  encodeCycleMarkers,
  filterMemoryRuleFor,
  hasCycleMarker,
  isRecordPath,
  keptQuery,
  normaliseMemoryPath,
  stateMemoryPath,
} from './index';
import {
  areCyclesSettled,
  bindFilterMemoryUser,
  currentMemoryCycle,
  filterMemoryVersion,
  getArrival,
  getFilterMemory,
  isFilterMemoryBoundTo,
  isFilterMemoryReadable,
  isFilterMemoryReady,
  isRecentUserInput,
  setArrival,
  setFilterMemory,
  stateMemoryKey,
  subscribeFilterMemory,
  touchFilterMemory,
  urlMemoryKey,
  type ArrivalKind,
} from './store';

/**
 * The URL half of filter memory, plus the two state hooks. Read `./index.ts`
 * for the rulings and `./store.ts` for storage.
 */

/** How long a restore may wait on `/cycles` to resolve a current-cycle marker. */
const CYCLE_WAIT_MS = 1500;

/** Params that never make an address a "link": paging only. */
const PAGING_KEYS = new Set(['page', 'perPage']);

/** Does the address carry nothing but paging? */
export function isBareSearch(search: string): boolean {
  for (const key of new URLSearchParams(search).keys()) {
    if (!PAGING_KEYS.has(key)) return false;
  }
  return true;
}

/**
 * How the reader arrived at this page. Pure, so a page's `useState`
 * initialiser can ask it during the very render the arrival is decided in:
 * once `<FilterMemory>` has recorded the arrival that record answers;
 * before, the address does (any non-paging param = a link).
 */
export function arrivalKindFor(pathname: string, search: string): ArrivalKind {
  const record = getArrival();
  if (record !== null && record.path === normaliseMemoryPath(pathname)) return record.kind;
  return isBareSearch(search) ? 'bare' : 'link';
}

/** Re-render whenever the store changes (bind, account read, cycles read). */
function useStoreVersion(): number {
  return useSyncExternalStore(subscribeFilterMemory, filterMemoryVersion, filterMemoryVersion);
}

/**
 * Has filter memory answered for this page's arrival?
 *
 * True when the remembered address was restored, or the reader's remembered
 * CLEAR stands on a bare arrival. False on a link arrival (ruling 3: the link
 * is shown exactly, and the page's own link behaviour is unchanged), and on a
 * partial restore whose current-cycle marker could not be resolved — the
 * page's own current-cycle default fills that in.
 *
 * `useSavedFilters` settles as `'remembered'` on true (ruling 4: saved filters
 * apply only when nothing is remembered); the default account lead, the
 * `/projects` and `/sales-tracker` cycle defaults stand down behind it.
 */
export function hasFilterMemory(pathname: string, search: string): boolean {
  const path = normaliseMemoryPath(pathname);
  if (filterMemoryRuleFor(path) === null) return false;
  const kind = arrivalKindFor(path, search);
  if (kind === 'link' || kind === 'restored-partial') return false;
  const key = urlMemoryKey(path);
  return key !== null && getFilterMemory('url', key) !== undefined;
}

interface RestorePlan {
  kind: ArrivalKind;
  /** Where to send the reader, or `null` when the URL stands as it is. */
  target: string | null;
  /** The kept query the page will hold once the plan lands. */
  kept: string;
  /** Waiting on `/cycles` to resolve a current-cycle marker. */
  waitForCycles: boolean;
}

function planArrival(
  pathname: string,
  search: string,
  hash: string,
  cycleWaitOver: boolean,
): RestorePlan {
  const path = normaliseMemoryPath(pathname);
  const rule = filterMemoryRuleFor(path);
  const params = new URLSearchParams(search);
  const bare = isBareSearch(search);
  const current = rule ? keptQuery(params, rule.keep) : '';
  const stand = (kind: ArrivalKind): RestorePlan => ({
    kind,
    target: null,
    kept: current,
    waitForCycles: false,
  });

  // Ruling 3: ANY param other than paging means the link wins — `?new=1`,
  // `?create=…`, a one-shot `companyId` — and nothing remembered is merged in.
  if (!bare) return stand('link');
  if (rule === null) return stand('bare');

  const entryKey = urlMemoryKey(path);
  const stored = entryKey === null ? undefined : getFilterMemory('url', entryKey);
  if (stored === undefined || stored === '') return stand('bare');

  const marker = hasCycleMarker(stored);
  if (marker && !areCyclesSettled() && !cycleWaitOver) {
    return { kind: 'bare', target: null, kept: current, waitForCycles: true };
  }
  const cycle = currentMemoryCycle();
  const remembered = new URLSearchParams(decodeCycleMarkers(stored, cycle));
  const next = new URLSearchParams(search);
  // A stale pager on an address being given different filters is meaningless.
  next.delete('page');
  for (const [key, value] of remembered) {
    if (!rule.keep.includes(key) || key === 'page') continue;
    next.append(key, value);
  }
  const kept = keptQuery(next, rule.keep);
  if (kept === '') return stand(marker && cycle === null ? 'restored-partial' : 'bare');
  const query = next.toString();
  return {
    kind: marker && cycle === null ? 'restored-partial' : 'restored',
    target: `${pathname}${query === '' ? '' : `?${query}`}${hash}`,
    kept,
    waitForCycles: false,
  };
}

/**
 * The URL half. Returns whether the page may render yet (`<FilterMemory>`
 * renders a skeleton until it may).
 *
 * ## How the restore beats every page-level arrival writer
 *
 * On a path WITH A RULE the page is held back while:
 *   - the store is not yet bound to the signed-in person (one layout pass,
 *     before paint — this one applies on every path, so table and search
 *     memory read a bound store at mount);
 *   - on a device with no local copy, the account read is out (5 s cap);
 *   - a current-cycle marker waits on `/cycles` (1.5 s cap, then the marker
 *     is dropped and the page's own current-cycle default fills it);
 *   - an arrival needs a restore: the redirect runs in a layout effect with
 *     `{ replace: true }`, so the page's FIRST render already sees the
 *     remembered URL. `useSavedFilters`, `useDefaultAccountLeadFilter` and the
 *     cycle defaults therefore never see the bare address.
 *
 * ## Arrival vs edit
 *
 * An arrival is a change of PATHNAME, or a PUSH navigation to the bare current
 * pathname (the nav row of the open page clicked — it restores, and never
 * stores a clear). Any other change on the same pathname is the page editing
 * its own params (pages write with `replace`), and is stored — that is what
 * lets a clear stay cleared. After a LINK arrival only real user edits (within
 * a moment of a key press, click or input) are stored, never what arrival
 * writers did to the link.
 */
export function useFilterMemory(): boolean {
  const { user, supportSession } = useAuth();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  useStoreVersion();

  /*
   * Owner 2026-10-23: while acting as somebody else, THEIR memory is restored
   * (read under the support token) and nothing is written. The store re-binds
   * — queue, arrival and copy reset — whenever the user OR the mode changes,
   * so nothing crosses between the super admin's memory and the target's.
   *
   * Between the support token landing and the target's identity loading,
   * `user` is still the super admin: bound to nobody (the page waits) rather
   * than to the admin's id over the target's document.
   */
  const acting = supportSession !== null;
  const ownId = user?.id ?? null;
  const switching = acting && ownId !== null && supportSession.actingAs.id !== ownId;
  const userId = switching ? null : ownId;
  useLayoutEffect(() => {
    bindFilterMemoryUser(userId, acting);
  }, [userId, acting]);

  const path = normaliseMemoryPath(location.pathname);
  const rule = filterMemoryRuleFor(path);
  const inert = userId === null;
  const bound = isFilterMemoryBoundTo(userId, acting);
  const active = !inert && bound && isFilterMemoryReadable();
  const ready = isFilterMemoryReady();

  const record = getArrival();
  const arriving =
    record === null ||
    record.path !== path ||
    (navigationType === 'PUSH' && record.key !== location.key && isBareSearch(location.search));

  /** The location key whose cycle wait has run out. */
  const [cycleWaitOverFor, setCycleWaitOverFor] = useState<string | null>(null);
  const cycleWaitOver = cycleWaitOverFor === location.key;

  /*
   * EVERY path waits for the account read on arrival — not only ruled ones.
   * *SUPERSEDED: "On a path WITH A RULE the page is held back…".* A path with
   * no URL rule (`/users`, `/contacts`, every record page) still restores
   * table and plain state at mount, and on a fresh device that state is only
   * in the account copy (2026-10-23: sorts missed there). Same caps as before:
   * 5 s with no local copy, `LOCAL_GRACE_MS` with one.
   */
  let plan: RestorePlan | null = null;
  if (active && arriving && ready) {
    plan = planArrival(location.pathname, location.search, location.hash, cycleWaitOver);
  }

  let blocked = false;
  if (switching) blocked = true;
  else if (!inert && !bound) blocked = true;
  else if (active && arriving && !ready) blocked = true;
  else if (plan !== null && (plan.target !== null || plan.waitForCycles)) blocked = true;

  const waitingForCycles = plan?.waitForCycles === true;
  const locationKey = location.key;
  useEffect(() => {
    if (!waitingForCycles) return;
    const timer = setTimeout(() => setCycleWaitOverFor(locationKey), CYCLE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitingForCycles, locationKey]);

  /*
   * Settle the arrival. No dependency list on purpose: it must re-check after
   * every store change while the page is held back, and it does nothing once
   * the arrival is recorded (`arriving` is then false).
   */
  useLayoutEffect(() => {
    if (!active || !arriving || plan === null || plan.waitForCycles) return;
    setArrival({ path, key: location.key, kind: plan.kind, kept: plan.kept });
    if (plan.target !== null) {
      const entryKey = urlMemoryKey(path);
      if (entryKey !== null) touchFilterMemory('url', entryKey);
      navigate(plan.target, { replace: true, state: location.state });
    }
  });

  // Store the page's own edits. The arrival itself is never stored.
  const keptNow = rule ? keptQuery(new URLSearchParams(location.search), rule.keep) : '';
  useEffect(() => {
    if (!active || rule === null) return;
    const current = getArrival();
    if (current === null || current.path !== path || current.kept === keptNow) return;
    setArrival({ ...current, kept: keptNow });
    // A link is read-only for memory: what saved filters, a cycle seed or the
    // account-lead seed did to it is not the reader's choice.
    if (current.kind === 'link' && !isRecentUserInput()) return;
    const entryKey = urlMemoryKey(path);
    if (entryKey !== null) {
      setFilterMemory('url', entryKey, encodeCycleMarkers(keptNow, currentMemoryCycle()));
    }
  }, [active, rule, path, keptNow]);

  return !blocked;
}

/* ── Component state ───────────────────────────────────────────────────── */

/**
 * May the state hooks restore on this arrival?
 *
 * - List pages: not on a LINK arrival (ruling 3: the link is shown exactly).
 * - Record pages: always. Their params (`?create=quote`, `?tab=…`) are
 *   one-shot actions, never what these hooks hold, so a record page reached
 *   by such a link still gets its remembered sorts and filters.
 */
export function mayRestoreState(pathname: string, search: string): boolean {
  if (isRecordPath(pathname)) return true;
  return arrivalKindFor(pathname, search) !== 'link';
}

/** The params by which a link names its own sort. */
const LINK_SORT_KEYS: readonly string[] = ['sort', 'order'];

/**
 * May a remembered TABLE SORT come back on this arrival? (owner 2026-10-23:
 * "the sort order should get stored in the preference store and persist".)
 *
 * Wider than `mayRestoreState`: a link such as `/companies?accountLeadId=5`
 * names funnels, not an order, so ruling 3 governs only what it names — its
 * funnels are shown exactly, and the person's remembered sort still applies.
 * Only a link that carries `sort`/`order` itself keeps the table unsorted
 * by memory.
 */
export function mayRestoreSort(pathname: string, search: string): boolean {
  if (mayRestoreState(pathname, search)) return true;
  const params = new URLSearchParams(search);
  return !LINK_SORT_KEYS.some((key) => params.has(key));
}

/**
 * Like `useState`, but remembered on the account per page under `id`
 * (preferences `filterMemoryState`).
 *
 * - The initial value comes from memory when there is any, else `initial`.
 *   On a LINK arrival at a list page it is always `initial` (ruling 3);
 *   record pages always restore (`mayRestoreState`).
 * - Every change is stored (debounced in the store); going back to `initial`
 *   is stored too, so a clear stays cleared.
 * - On record pages the key is the record TYPE's template path
 *   (`stateMemoryPath`: `/companies/782` → `/companies/record`), so one
 *   choice holds for every record of that type. Use an `id` that names the
 *   section (`notes-sort`, not one per record).
 * - While acting as another user, THEIR value is restored and nothing is
 *   written (owner 2026-10-23); nothing at all when `id` is `null`.
 * - Memory that lands AFTER mount (the account read on a fresh device,
 *   another tab) is applied while the person has not changed the value yet.
 * - `parse` validates what comes back; return `undefined` to reject it. The
 *   default accepts a value of the same `typeof` as `initial` (plus `null`
 *   when `initial` is `null`).
 *
 * Values are JSON, at most 2000 characters. Never remember a page number.
 */
export function useRememberedState<T>(
  id: string | null,
  initial: T | (() => T),
  parse?: (raw: unknown) => T | undefined,
): [T, Dispatch<SetStateAction<T>>] {
  const location = useLocation();
  useStoreVersion();
  const path = stateMemoryPath(location.pathname);
  const key = id === null ? null : stateMemoryKey(path, `state-${id}`);

  const [setup] = useState(() => ({
    fallback: typeof initial === 'function' ? (initial as () => T)() : initial,
    restorable: mayRestoreState(location.pathname, location.search),
  }));
  const parseRef = useRef(parse);
  parseRef.current = parse;

  /** A stored payload as a value: `''` is the remembered clear; `undefined` = reject. */
  const decode = (raw: string): T | undefined => {
    const fallback = setup.fallback;
    if (raw === '') return fallback;
    try {
      const parsed: unknown = JSON.parse(raw);
      const check = parseRef.current;
      return check
        ? check(parsed)
        : parsed === null
          ? fallback === null
            ? (parsed as T)
            : undefined
          : typeof parsed === typeof fallback
            ? (parsed as T)
            : undefined;
    } catch {
      return undefined;
    }
  };

  const [value, setValue] = useState<T>(() => {
    if (key === null || !setup.restorable) return setup.fallback;
    const raw = getFilterMemory('state', key);
    if (raw === undefined) return setup.fallback;
    return decode(raw) ?? setup.fallback;
  });

  const signature: string | undefined = JSON.stringify(value);
  const stored = useRef<{ key: string | null; signature: string | undefined }>({ key, signature });
  /** The person changed this value since mount: memory never overrides them. */
  const touched = useRef(false);
  useEffect(() => {
    if (key === null) return;
    if (stored.current.key !== key) {
      stored.current = { key, signature };
      touched.current = false;
      return;
    }
    if (stored.current.signature === signature) return;
    stored.current = { key, signature };
    touched.current = true;
    setFilterMemory('state', key, signature === undefined ? '' : signature);
  }, [key, signature]);

  // Late memory: applied to an untouched value, and never counted as a change.
  const latestRaw = key === null || !setup.restorable ? undefined : getFilterMemory('state', key);
  useEffect(() => {
    if (key === null || touched.current || latestRaw === undefined) return;
    if (stored.current.key !== key) return;
    const next = decode(latestRaw);
    if (next === undefined) return;
    const nextSignature: string | undefined = JSON.stringify(next);
    if (nextSignature === stored.current.signature) return;
    stored.current = { key, signature: nextSignature };
    // oxlint-disable-next-line react/set-state-in-effect
    setValue(next);
    // `decode` reads refs and mount-time setup only, so it is not a dependency.
  }, [key, latestRaw]);

  return [value, setValue];
}

/** A short stable lowercase hash (djb2, base 36). */
function shortHash(text: string): string {
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash * 33) ^ text.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
}

/**
 * A stored table blob, narrowed to what this arrival may restore: everything,
 * the sort alone (`{ s }`, a link that names funnels but no sort), or nothing.
 * `''` (a remembered clear) is `{}`, so a clear can be applied late too.
 */
function restrictTableBlob(
  raw: string | undefined,
  restore: 'all' | 'sort' | 'none',
): unknown {
  if (raw === undefined || restore === 'none') return undefined;
  if (raw === '') return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
  if (restore === 'all') return parsed;
  if (parsed === null || typeof parsed !== 'object') return undefined;
  return { s: (parsed as { s?: unknown }).s ?? null };
}

/**
 * The `useTableFilters` half: one remembered JSON blob per table instance,
 * keyed by page (`stateMemoryPath`, so a record page shares one entry with
 * every record of its type) and EITHER the table's own `memoryId` (stable
 * whatever columns it shows) OR a hash of its column keys (two tables on one
 * page get two entries; two with identical columns on one page share one).
 *
 * - `initial`: the blob at mount — everything on a bare/restored arrival or a
 *   record page; ONLY the sort (`{ s }`) on a link arrival that names no
 *   `sort`/`order` (`mayRestoreSort`); `undefined` otherwise.
 * - `latest`: the same, re-read on every store change and every key change,
 *   so memory that lands after mount (account read, another tab) or columns
 *   that arrive late (a new hash) can still be applied. Stable identity while
 *   the stored payload is unchanged.
 * - `remember`: writes to the CURRENT key.
 *
 * *SUPERSEDED (2026-10-23): "The key is fixed at mount".* A table whose
 * columns arrived after mount kept the partial set's key for life and never
 * found the entry written under its full set.
 */
export function useRememberedTableState(
  columnSignature: string,
  enabled: boolean,
  memoryId?: string,
): {
  initial: unknown;
  latest: unknown;
  remember: (blob: string) => void;
} {
  const location = useLocation();
  useStoreVersion();
  const path = stateMemoryPath(location.pathname);
  const suffix =
    memoryId !== undefined && memoryId !== ''
      ? `table-id-${memoryId}`
      : `table-${shortHash(columnSignature)}`;
  const key = !enabled ? null : stateMemoryKey(path, suffix);
  const [restore] = useState<'all' | 'sort' | 'none'>(() =>
    mayRestoreState(location.pathname, location.search)
      ? 'all'
      : mayRestoreSort(location.pathname, location.search)
        ? 'sort'
        : 'none',
  );
  const raw = key === null ? undefined : getFilterMemory('state', key);
  const latest = useMemo(() => restrictTableBlob(raw, restore), [raw, restore]);
  const [initial] = useState<unknown>(() => latest);
  const keyRef = useRef(key);
  keyRef.current = key;
  const remember = useCallback((blob: string) => {
    const current = keyRef.current;
    if (current !== null) setFilterMemory('state', current, blob);
  }, []);
  return { initial, latest, remember };
}
